<template>
  <main class="member-reservations">
    <div class="mr-inner">

      <!-- 401 未登入 -->
      <div v-if="isUnauthorized" class="mr-state mr-state--warn">
        <p class="mr-state__title">請先登入</p>
        <p class="mr-state__msg">查看我的預約需要會員登入。</p>
        <router-link
          :to="{ name: 'LoginPage', query: { redirect: '/member/reservations' } }"
          class="mr-btn mr-btn--primary"
        >
          前往登入
        </router-link>
      </div>

      <!-- 載入中 -->
      <div v-else-if="bookingStore.memberReservationsLoading" class="mr-state">
        <span class="mr-spinner" />
        <p>載入預約記錄…</p>
      </div>

      <!-- 其他錯誤 -->
      <div v-else-if="bookingStore.memberReservationsError" class="mr-state mr-state--error">
        <p class="mr-state__title">載入失敗</p>
        <p class="mr-state__msg">{{ bookingStore.memberReservationsError }}</p>
        <button class="mr-btn mr-btn--secondary" @click="load">重新載入</button>
      </div>

      <!-- 內容 -->
      <template v-else>
        <h1 class="mr-title">我的預約</h1>

        <!-- 空列表 -->
        <div v-if="bookingStore.memberReservations.length === 0" class="mr-empty">
          <p>目前沒有任何預約記錄</p>
          <router-link :to="{ name: 'BookingHome' }" class="mr-btn mr-btn--primary">
            立即預約
          </router-link>
        </div>

        <template v-else>
          <!-- 即將到來 -->
          <section v-if="upcoming.length > 0" class="mr-section">
            <h2 class="mr-section-title">即將到來</h2>
            <div class="mr-list">
              <div
                v-for="r in upcoming"
                :key="r.id"
                class="mr-card"
              >
                <ReservationCard
                  :reservation="r"
                  :cancel-loading="cancelLoading[r.reservation_code]"
                  :cancel-error="cancelErrors[r.reservation_code]"
                  :can-cancel="canCancel(r)"
                  @cancel="onCancel(r.reservation_code)"
                />
              </div>
            </div>
          </section>

          <!-- 歷史紀錄 -->
          <section v-if="history.length > 0" class="mr-section">
            <h2 class="mr-section-title">歷史紀錄</h2>
            <div class="mr-list">
              <div
                v-for="r in history"
                :key="r.id"
                class="mr-card mr-card--muted"
              >
                <ReservationCard
                  :reservation="r"
                  :can-cancel="false"
                />
              </div>
            </div>
          </section>
        </template>
      </template>

    </div>
  </main>
</template>

<!-- ── 子元件：單筆預約卡片 ─────────────────────────── -->
<script>
// 定義在同一個檔案，避免多開一個 .vue 檔
const ReservationCard = {
  name: 'ReservationCard',
  props: {
    reservation:   { type: Object,  required: true },
    canCancel:     { type: Boolean, default: false },
    cancelLoading: { type: Boolean, default: false },
    cancelError:   { type: String,  default: '' },
  },
  emits: ['cancel'],
  computed: {
    statusLabel() {
      return { pending: '待確認', confirmed: '已確認', completed: '已完成', cancelled: '已取消' }[this.reservation.status] ?? this.reservation.status
    },
    fmtTime() {
      return (t) => t ? t.slice(0, 5) : ''
    },
  },
  template: `
    <div class="rc">
      <div class="rc__top">
        <span class="rc__status" :class="'rc__status--' + reservation.status">
          {{ statusLabel }}
        </span>
        <p class="rc__code">{{ reservation.reservation_code }}</p>
      </div>
      <p class="rc__store">{{ reservation.store_name }}</p>
      <p class="rc__service">
        {{ reservation.service_name }}
        <span v-if="reservation.service_item_name" class="rc__sub"> · {{ reservation.service_item_name }}</span>
        <span v-if="reservation.duration_minutes" class="rc__sub">
          （約 {{ reservation.duration_minutes }} 分鐘）
        </span>
      </p>
      <p class="rc__time">
        {{ reservation.slot_date }} &nbsp;
        {{ fmtTime(reservation.start_time) }}
        <span v-if="reservation.end_time"> – {{ fmtTime(reservation.end_time) }}</span>
      </p>
      <p v-if="reservation.store_phone" class="rc__phone">
        店家電話：
        <a :href="'tel:' + reservation.store_phone">{{ reservation.store_phone }}</a>
      </p>

      <p v-if="cancelError" class="rc__cancel-error">{{ cancelError }}</p>

      <button
        v-if="canCancel"
        class="rc__cancel-btn"
        :disabled="cancelLoading"
        @click="$emit('cancel')"
      >
        <span v-if="cancelLoading" class="rc__spinner" />
        {{ cancelLoading ? '取消中…' : '取消預約' }}
      </button>
    </div>
  `,
}
</script>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useBookingStore } from '@/store/bookingStore'

const bookingStore = useBookingStore()

// ── 401 判斷 ────────────────────────────────────────
// 依 store 的 memberUnauthorized flag（由 HTTP status 401 觸發），
// 不靠錯誤訊息字串比對，避免訊息改變時判斷失效。
const isUnauthorized = computed(() => bookingStore.memberUnauthorized)

// ── 分組 ─────────────────────────────────────────────
const todayTW = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' })

const upcoming = computed(() =>
  bookingStore.memberReservations.filter(r =>
    ['pending', 'confirmed'].includes(r.status) &&
    r.slot_date >= todayTW
  ).sort((a, b) => {
    if (a.slot_date !== b.slot_date) return a.slot_date.localeCompare(b.slot_date)
    return a.start_time.localeCompare(b.start_time)
  })
)

const history = computed(() =>
  bookingStore.memberReservations.filter(r =>
    !['pending', 'confirmed'].includes(r.status) ||
    r.slot_date < todayTW
  ).sort((a, b) => {
    if (a.slot_date !== b.slot_date) return b.slot_date.localeCompare(a.slot_date)
    return b.start_time.localeCompare(a.start_time)
  })
)

// ── 是否可取消（前端預判）────────────────────────────
// 規則：status 為 pending/confirmed，且距 slot 開始 > 48 小時
// 後端仍會做最終驗證，此處只決定是否顯示按鈕。
function canCancel(r) {
  if (!['pending', 'confirmed'].includes(r.status)) return false
  const slotStart    = new Date(`${r.slot_date}T${r.start_time}+08:00`)
  const cancelCutoff = new Date(Date.now() + 48 * 60 * 60 * 1000)
  return slotStart > cancelCutoff
}

// ── 取消狀態（每筆獨立）──────────────────────────────
const cancelLoading = ref({})  // { [reservation_code]: boolean }
const cancelErrors  = ref({})  // { [reservation_code]: string }

async function onCancel(code) {
  cancelLoading.value[code] = true
  cancelErrors.value[code]  = ''

  const result = await bookingStore.cancelReservation(code)

  if (!result.success) {
    // 顯示後端回傳訊息（例如「距離預約時間不足 24 小時，請直接聯絡店家取消」）
    cancelErrors.value[code] = result.message || '取消失敗，請稍後再試'
  }
  cancelLoading.value[code] = false
}

// ── 初始化 ───────────────────────────────────────────
async function load() {
  await bookingStore.fetchMemberReservations()
}

onMounted(load)
</script>

<style scoped>
.member-reservations { padding: 24px 16px 80px; }
.mr-inner {
  max-width: 680px;
  margin: 0 auto;
}

/* 狀態 */
.mr-state {
  text-align: center;
  padding: 64px 16px;
  color: #888;
}
.mr-state--warn  .mr-state__title { color: #e67e22; font-size: 1.2rem; margin-bottom: 8px; }
.mr-state--error .mr-state__title { color: #c0392b; font-size: 1.2rem; margin-bottom: 8px; }
.mr-state__msg { margin-bottom: 24px; color: #888; font-size: 0.95rem; }
.mr-spinner {
  display: inline-block;
  width: 32px; height: 32px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 標題 */
.mr-title {
  font-size: 1.7rem;
  color: #333;
  margin: 0 0 28px;
}

/* 空列表 */
.mr-empty {
  text-align: center;
  padding: 48px 0;
  color: #888;
}
.mr-empty p { margin-bottom: 20px; }

/* 分組 */
.mr-section { margin-bottom: 40px; }
.mr-section-title {
  font-size: 1rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  border-bottom: 1px solid #ede7e2;
  padding-bottom: 8px;
  margin-bottom: 16px;
}

.mr-list { display: flex; flex-direction: column; gap: 12px; }
.mr-card--muted { opacity: 0.75; }

/* 按鈕 */
.mr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 24px;
  border-radius: 8px;
  border: none;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;
}
.mr-btn--primary   { background: #8b6f5c; color: #fff; }
.mr-btn--primary:hover { background: #7a5f4d; }
.mr-btn--secondary { background: transparent; border: 1px solid #8b6f5c; color: #8b6f5c; }
.mr-btn--secondary:hover { background: #f5ede8; }
</style>

<!-- 子元件樣式（非 scoped，需要 :deep 或全域注入；此處以 global style 補丁方式） -->
<style>
/* ReservationCard 樣式 */
.rc {
  background: #fff;
  border: 1px solid #ede7e2;
  border-radius: 12px;
  padding: 16px 18px;
}
.rc__top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.rc__code {
  font-size: 0.78rem;
  color: #bbb;
  margin: 0;
  word-break: break-all;
}
.rc__status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}
.rc__status--pending   { background: #fef3cd; color: #856404; }
.rc__status--confirmed { background: #d1ecf1; color: #0c5460; }
.rc__status--completed { background: #d4edda; color: #155724; }
.rc__status--cancelled { background: #f8d7da; color: #721c24; }
.rc__store {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px;
}
.rc__service {
  font-size: 0.9rem;
  color: #555;
  margin: 0 0 4px;
}
.rc__sub { font-size: 0.85em; color: #aaa; }
.rc__time {
  font-size: 0.9rem;
  color: #8b6f5c;
  margin: 0 0 4px;
  font-weight: 500;
}
.rc__phone {
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 12px;
}
.rc__phone a { color: #8b6f5c; text-decoration: none; }
.rc__phone a:hover { text-decoration: underline; }
.rc__cancel-error {
  font-size: 0.85rem;
  color: #c0392b;
  background: #fdf0ef;
  border: 1px solid #f5c6c3;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0;
  line-height: 1.5;
}
.rc__cancel-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 18px;
  border-radius: 7px;
  border: 1px solid #c0392b;
  background: transparent;
  color: #c0392b;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.15s;
}
.rc__cancel-btn:hover:not(:disabled) { background: #fdf0ef; }
.rc__cancel-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.rc__spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(192,57,43,0.3);
  border-top-color: #c0392b;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
</style>
